import {
  FormGroup,
  FormHelperText,
  FormLabel,
  FormControlProps,
  CheckboxProps,
  FormGroupProps,
  Button,
} from "@mui/material"
import Checkbox from "@mui/material/Checkbox"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import { isEmpty } from "lodash"
import { Controller, FieldValues, UseControllerProps } from "react-hook-form"

type Option = {
  value: string | number
  label: string
}

export interface ControlledCheckBoxProps<FieldValueProps extends FieldValues>
  extends UseControllerProps<FieldValueProps> {
  label?: string
  options: Option[]
  allowSelectAll?: boolean
  FormControlProps?: FormControlProps
  FormGroupProps?: FormGroupProps
  CheckboxProps?: CheckboxProps
}

function ControlledCheckboxGroup<FieldValueProps extends FieldValues>({
  options,
  control,
  name,
  label,
  allowSelectAll = false,
  FormControlProps = {},
  FormGroupProps = {},
  CheckboxProps = {},
}: ControlledCheckBoxProps<FieldValueProps>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({
        fieldState: { error },
        field: { onChange, value, onBlur },
      }) => (
        <FormControl
          component={"fieldset" as "div"}
          variant="standard"
          {...FormControlProps}
          error={!isEmpty(error)}
        >
          {allowSelectAll ? (
            <FormLabel component="legend">
              {label}{" "}
              <Button
                onClick={() => {
                  onChange([...options.map((opt) => opt.value)])
                }}
                variant="text"
              >
                Select All
              </Button>
            </FormLabel>
          ) : (
            <FormLabel component="legend">{label}</FormLabel>
          )}
          <FormGroup {...FormGroupProps}>
            {options.map((checkbox) => (
              <FormControlLabel
                key={checkbox.value}
                control={
                  <Checkbox
                    sx={{
                      color: "primary.main",
                      "&.Mui-checked": {
                        color: "primary.main",
                      },
                    }}
                    checked={value?.includes(checkbox.value)}
                    onChange={(e, checked) => {
                      if (checked) {
                        onChange([...value, checkbox.value])
                      } else {
                        onChange(
                          value?.filter((v: string) => v !== checkbox.value)
                        )
                      }
                      onBlur()
                    }}
                    name={String(checkbox.value)}
                    {...CheckboxProps}
                  />
                }
                label={checkbox.label}
                onChange={onChange}
              />
            ))}
          </FormGroup>
          <FormHelperText error>{error?.message ?? ""}</FormHelperText>
        </FormControl>
      )}
    />
  )
}

export default ControlledCheckboxGroup
